import { ESCAPE } from '@angular/cdk/keycodes';
import { BlockScrollStrategy, GlobalPositionStrategy, Overlay, OverlayConfig, ViewportRuler } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Injector, StaticProvider, Type } from '@angular/core';
import { GenericModalComponent } from '@app/ui-components/modals/generic-modal/generic-modal.component';
import { MessageBoxContentComponent, MessageBoxData } from '@app/ui-components/modals/message-box-content/message-box-content.component';
import { MessageBoxIcon, ModalButton, ModalButtons, ModalCloseReason, ModalRef } from '../models/modal-ref';
import { ErrorData, ModalErrorData, ModalerrorinterceptorComponent } from '@app/shared/modals/modalerrorinterceptor/modalerrorinterceptor.component';
import { FormGroup } from '@angular/forms';

/**
 * Allows displaying modals and MessageBoxes
 */
@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private openedModals: ModalRef[] = [];

  constructor(
    private overlay: Overlay,
    private viewportRuler: ViewportRuler,
    @Inject(DOCUMENT) private document: Document,
    private injector: Injector
  ) {
    // Close modal on escape press
    // Normally we could do this inside the modal but we cannot guarantee that the focus stays inside the modal
    // (and therefore it won't receive the event). Maybe the CdkTrapFocus gets improved in future to keep the focuse inside in every case.
    this.document.addEventListener('keydown', (event) => {
      if (event.keyCode === ESCAPE) {
        // Find modal which is most likely the modal which is visible to the user
        const modalRef = this.openedModals[this.openedModals.length - 1];

        if (modalRef && modalRef.modalConfig.closeOnBackdropClick) {
          modalRef.close(ModalCloseReason.BackdropClick);
        }
      }
    });
  }

  /**
   * Shows a modal.
   *
   * Example for a modal that uses a component (`ModalContentComponent`) as modal content:
   *
   * In this case, `ModalContentComponent` requests an instance of `DataClass`
   * to be injected (Because it's a dependency in the constructor of `ModalContentComponent`).
   *
   * Note: The ModalRef instance is always injected into the component and can therefore be used if required.
   *
   * ```typescript
   * const classInstance = new DataClass();
   * // The properties can be accessed by the elements inside the ModalContentComponent
   * classInstance.someProperty = 'value';
   *
   * this.show({
   *   content: ModalContentComponent,
   *   title: 'Title',
   *   buttons: ModalButtons.Ok,
   *   closeOnBackdropClick: true,
   *   stringsAreTranslationKeys: false,
   *   defaultButton: ModalButton.Ok,
   *   // The classes that are injected into the ModalContentComponent (ModalRef is automatically injected)
   *   staticProviders: [{provide: DataClass, useValue: classInstance}]
   * });
   * ```
   */
  public show(modalConfig: ModalConfig): ModalRef {
    // Default values
    modalConfig.closeOnBackdropClick      = modalConfig.closeOnBackdropClick ?? true;
    modalConfig.stringsAreTranslationKeys = modalConfig.stringsAreTranslationKeys ?? true;
    modalConfig.sizing                    = modalConfig.sizing ?? 'default';
    modalConfig.blockSubmitOnInvalid      ??= true;

    // Automatically infer a default button if no one is given
    if (modalConfig.defaultButton == null) {
      switch (modalConfig.buttons) {
        case ModalButtons.Ok:
        case ModalButtons.OkCancel:
          modalConfig.defaultButton = ModalButton.Ok;
          break;
        case ModalButtons.YesNo:
        case ModalButtons.YesNoCancel:
          modalConfig.defaultButton = ModalButton.Yes;
          break;
      }
    }

    // Automatically infer a default cancel button if no one is given
    if (modalConfig.defaultCancelButton == null) {
      switch (modalConfig.buttons) {
        case ModalButtons.Ok:
          modalConfig.defaultCancelButton = ModalButton.Ok;
          break;
        case ModalButtons.OkCancel:
          modalConfig.defaultCancelButton = ModalButton.Cancel;
          break;
        case ModalButtons.YesNo:
          modalConfig.defaultCancelButton = ModalButton.No;
          break;
        case ModalButtons.YesNoCancel:
          modalConfig.defaultCancelButton = ModalButton.Cancel;
          break;
      }
    }

    modalConfig.submitButton ??= modalConfig.defaultButton;

    const positionStrategy = new GlobalPositionStrategy();
    positionStrategy.centerHorizontally().centerVertically();
    const overlayConfig: OverlayConfig = {
      positionStrategy,
      scrollStrategy: new BlockScrollStrategy(this.viewportRuler, this.document),
      panelClass: 'scg-overlay-pane',
      hasBackdrop: true
    };
    const overlayRef = this.overlay.create(overlayConfig);
    const modalRef = new ModalRef(
      overlayRef,
      modalConfig,
      new FormGroup({})
    );

    // The modalRef that is injected here is also injected into all child components of GenericModalComponent
    // which means it can also be accessed by the content component
    const injector = Injector.create({ providers: [{ provide: ModalRef, useValue: modalRef }], parent: this.injector });
    overlayRef.attach(new ComponentPortal(GenericModalComponent, null, injector));

    this.openedModals.push(modalRef);
    modalRef.onClose.subscribe(() => {
      const index = this.openedModals.indexOf(modalRef);
      this.openedModals.splice(index, 1);
    });

    return modalRef;
  }

  /**
   * Shows a MessageBox
   */
  public showMessageBox(messageBoxConfig: MessageBoxConfig): ModalRef {
    // Default values
    messageBoxConfig.closeOnBackdropClick = messageBoxConfig.closeOnBackdropClick ?? true;
    messageBoxConfig.stringsAreTranslationKeys = messageBoxConfig.stringsAreTranslationKeys ?? true;

    const messageBoxData = new MessageBoxData();
    messageBoxData.text = messageBoxConfig.text;
    messageBoxData.icon = messageBoxConfig.icon;

    return this.show({
      content: MessageBoxContentComponent,
      title: messageBoxConfig.title,
      buttons: messageBoxConfig.buttons,
      closeOnBackdropClick: messageBoxConfig.closeOnBackdropClick,
      stringsAreTranslationKeys: messageBoxConfig.stringsAreTranslationKeys,
      defaultButton: messageBoxConfig.defaultButton,
      defaultCancelButton: messageBoxConfig.defaultCancelButton,
      staticProviders: [{provide: MessageBoxData, useValue: messageBoxData}]
    });
  }

  public showErrorMessageBox(errorData: ErrorData) {
    const errorDataClass = new ModalErrorData(errorData);

    this.show({
      content: ModalerrorinterceptorComponent,
      title: 'ERROR.TITLE',
      buttons: ModalButtons.Ok,
      defaultButton: ModalButton.Ok,
      staticProviders: [{provide: ModalErrorData, useValue: errorDataClass}]
    });
  }
}


export interface ModalConfig {
  /**
   * Content of the modal (string or component that contains the modal content)
   */
  content: string | Type<any>;
  /**
   * Title of the modal
   */
  title: string;
  /**
   * Buttons that are displayed at the bottom of the modal
   */
  buttons: ModalButtons;
  /**
   * If true, the modal can be closed by clicking on the backdrop, by using the close button or by pressing escape.
   *
   * Default: true
   */
  closeOnBackdropClick?: boolean;
  /**
   * If true, the content (if string) and title arguments are expected to be translation keys instead of real text.
   *
   * Default: true
   */
  stringsAreTranslationKeys?: boolean;
  /**
   * Button that is focused by default.
   *
   * If `buttons` are `None`, this value is ignored.
   *
   * Default: The value is automatically inferred so it fits the chosen `buttons`
   */
  defaultButton?: ModalButton;
  /**
   * Clicks on backdrop, the close button or pressing escape will emit as the given button.
   *
   * If `buttons` are `None`, this value is ignored.
   *
   * Default: The value is automatically inferred so it fits the chosen `buttons`
   */
   defaultCancelButton?: ModalButton;
   /**
    * Button that is used to submit the entered data.
    * The submit button is equal to the default button by default.
    * 
    * Only set the submit button manually if the default button is not the submit button
    * (e.g. if pressing enter should not submit the entered data and instead trigger cancel).
    * In that case, the submit button should be the button that is used to submit the data.
    * 
    * Default: Equal to defaultButton
    */
   submitButton?: ModalButton;
   /**
    * By default, the submit button is disabled (and pressing enter is blocked if default button is equal to submit button)
    * if the entered data is invalid.
    * Set to false, if the submit button should stay enabled.
    * 
    * Default: true
    */
   blockSubmitOnInvalid?: boolean;
   /**
    * Size of the modal.
    * 
    * - `default`: Default modal size
    * - `pagesize`: Modal is allowed to use almost the full page size
    * 
    * Default: `'default'`
    */
   sizing?: 'default'|'pagesize';
  /**
   * Data that is injected into the modal content component (if the `content` property is a component).
   * See Angular documentation Injector or StaticProvider.
   */
  staticProviders?: StaticProvider[];
}

export interface MessageBoxConfig {
  /**
   * Text of the MessageBox (translation key or actual text)
   */
  text: string;
  /**
   * Title of the MessageBox (translation key or actual text)
   */
  title: string;
  /**
   * Buttons that are displayed at the bottom of the MessageBox
   */
  buttons: ModalButtons;
  /**
   * Button that is focused by default.
   *
   * Clicks on backdrop are also emitted as click on default button.
   */
  defaultButton?: ModalButton;
  /**
   * Clicks on backdrop and the close button will emit as the given button.
   * If `buttons` are `None` or `closeOnBackdropClick` is `false`, this value is ignored.
   *
   * If not given, the defaultCancelButton will be equal to the `defaultButton`.
   */
  defaultCancelButton?: ModalButton;
  /**
   * Icon that is displayed next to the MessageBox text
   */
  icon: MessageBoxIcon;
  /**
   * If true, the MessageBox can be closed by clicking on the backdrop or by using the close button.
   *
   * Default: true
   */
  closeOnBackdropClick?: boolean;
  /**
   * If true, the text and title arguments are expected to be translation keys instead of real text.
   *
   * Default: true
   */
  stringsAreTranslationKeys?: boolean;
}
